/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @repo/internal/styles/consistent-style-ordering */
import { type ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';

import Heading, { type HeadingProps } from '@atlaskit/heading/heading';
import LinkIcon from '@atlaskit/icon/core/link';
import { Inline } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { BrandHeading } from '../brand-heading';
import ClickCopyAnchor from '../click-copy-anchor';
import { useRegisterHeading } from '../heading-context/use-register-heading';

const sectionLinkStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		[media.above.md]: {
			marginInlineStart: token('space.negative.400'),
			padding: 0,
			opacity: 0,
			position: 'absolute',
			transform: 'translateX(4px)',
			transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
			'&:focus': {
				opacity: 1,
			},
		},
	},
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		button: {
			opacity: 1,
			transitionDuration: '0.1s',
			transform: 'none',
		},
	},
	/**
	 * We want to apply vertical space between each section, so that content within a section is visually grouped, and sections
	 * have visual separation from each other. We can do this by applying a `margin-top` to `SectionLink`, however
	 * mainContentStyles already has a `padding-top: 32px;` which creates too much vertical space on the first element of MDX content
	 * As these section headers are wrapped in <div>s the standard CSS reset doesn't apply, so we handle the first-child differently here.
	 */
	marginBlockStart: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-child:is(div), style:first-child + :is(div)': {
		marginBlockStart: token('space.0'),
	},
});

const h2Styles = css({
	/**
	 * We want to apply vertical space between each section, so that content within a section is visually grouped, and sections
	 * have visual separation from each other. We can do this by applying a `margin-top` to `SectionLink`, however
	 * mainContentStyles already has a `padding-top: 32px;` which creates too much vertical space on the first element of MDX content
	 * As these section headers are wrapped in <div>s the standard CSS reset doesn't apply, so we handle the first-child differently here.
	 */
	marginBlockStart: token('space.400'),
	marginBlockEnd: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-child:is(div), style:first-child + :is(div)': {
		marginBlockStart: token('space.0'),
	},
});

const h3Styles = css({
	/**
	 * We want to apply vertical space between each section, so that content within a section is visually grouped, and sections
	 * have visual separation from each other. We can do this by applying a `margin-top` to `SectionLink`, however
	 * mainContentStyles already has a `padding-top: 32px;` which creates too much vertical space on the first element of MDX content
	 * As these section headers are wrapped in <div>s the standard CSS reset doesn't apply, so we handle the first-child differently here.
	 */
	marginBlockStart: token('space.200'),
	marginBlockEnd: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-child:is(div), style:first-child + :is(div)': {
		marginBlockStart: token('space.0'),
	},
});

const h4Styles = css({
	/**
	 * We want to apply vertical space between each section, so that content within a section is visually grouped, and sections
	 * have visual separation from each other. We can do this by applying a `margin-top` to `SectionLink`, however
	 * mainContentStyles already has a `padding-top: 32px;` which creates too much vertical space on the first element of MDX content
	 * As these section headers are wrapped in <div>s the standard CSS reset doesn't apply, so we handle the first-child differently here.
	 */
	marginBlockStart: token('space.100'),
	marginBlockEnd: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-child:is(div), style:first-child + :is(div)': {
		marginBlockStart: token('space.0'),
	},
});

const styleMap = {
	2: h2Styles,
	3: h3Styles,
	4: h4Styles,
};

type SupportedDepths = 2 | 3 | 4;

type SectionHeadingLinkProps = {
	id: string;
	children: ReactNode;
	clickToCopyTestId?: string;
	depth: SupportedDepths;
	/**
	 * An optional heading value, incase
	 * children is passed as an object
	 */
	heading?: string;
	css?: any;
	isBrandHeading?: boolean;
};

const headingLevel: Record<
	SupportedDepths,
	Extract<Required<HeadingProps>['size'], 'large' | 'medium' | 'small'>
> = {
	2: 'large',
	3: 'medium',
	4: 'small',
};

/* Brand headings are larger in nature, so map down one size compared to regular headings to avoid them being huge */
const headingLevelBrand: Record<
	SupportedDepths,
	Extract<Required<HeadingProps>['size'], 'medium' | 'small' | 'xsmall'>
> = {
	2: 'medium',
	3: 'small',
	4: 'xsmall',
};

/**
 * __SectionHeadingLink__
 *
 * A heading with a link icon that allows users to copy the link to the heading.
 * The heading will be added to the local navigation if it is a heading 2 or 3.
 */
const SectionHeadingLink = ({
	id,
	children,
	heading,
	depth,
	clickToCopyTestId,
	isBrandHeading,
}: SectionHeadingLinkProps): React.JSX.Element => {
	useRegisterHeading({
		id,
		value: heading || children,
		depth,
		enabled: Boolean(children) && depth !== 4,
	});

	const size = isBrandHeading ? headingLevelBrand[depth] : headingLevel[depth];
	const as: 'h2' | 'h3' | 'h4' = `h${depth}`;

	return (
		<div id={id} css={[sectionLinkStyles, styleMap[depth]]}>
			<Inline space="space.025" alignBlock="center">
				<ClickCopyAnchor href={`#${id}`} testId={clickToCopyTestId}>
					<LinkIcon
						spacing="spacious"
						label={`Copy link to '${heading || children?.toString()}'`}
						color={token('color.link')}
					/>
				</ClickCopyAnchor>
				{isBrandHeading ? (
					<BrandHeading size={size} as={as} shouldOwnAnchor={false}>
						{children}
					</BrandHeading>
				) : (
					<Heading size={size}>{children}</Heading>
				)}
			</Inline>
		</div>
	);
};

export default SectionHeadingLink;
